// UI Controller - Handles DOM manipulation and user interactions

if (window.__debug__) {
  console.debug("UIController loading...");
}

const UIController = {
  elements: {},
  selectedCell: null,

  // Initialize UI elements
  init() {
    if (window.__debug__) {
      console.debug("UIController.init() called");
    }
    this.cacheElements();
    if (window.__debug__) {
      console.debug("Elements cached:", Object.keys(this.elements));
    }
  },

  // Cache DOM elements
  cacheElements() {
    this.elements = {
      homeScreen: document.getElementById("home-screen"),
      gameScreen: document.getElementById("game-screen"),
      sudokuGrid: document.getElementById("sudoku-grid"),
      difficultyBtns: document.querySelectorAll(".difficulty-btn"),
      continueBtn: document.getElementById("continue-btn"),
      settingsBtn: document.getElementById("settings-btn"),
      statsBtn: document.getElementById("stats-btn"),
      backBtn: document.getElementById("back-btn"),
      hintBtn: document.getElementById("hint-btn"),
      pauseBtn: document.getElementById("pause-btn"),
      keypadBtns: document.querySelectorAll(".keypad-btn"),
      undoBtn: document.getElementById("undo-btn"),
      difficultyDisplay: document.getElementById("difficulty-display"),
      timerDisplay: document.getElementById("timer-display"),
      pauseOverlay: document.getElementById("pause-overlay"),
      resumeBtn: document.getElementById("resume-btn"),
      quitBtn: document.getElementById("quit-btn"),
      victoryModal: document.getElementById("victory-modal"),
      victoryTime: document.getElementById("victory-time"),
      newGameBtn: document.getElementById("new-game-btn"),
      menuBtn: document.getElementById("menu-btn"),
      settingsModal: document.getElementById("settings-modal"),
      timerToggle: document.getElementById("timer-toggle"),
      hintsToggle: document.getElementById("hints-toggle"),
      highlightToggle: document.getElementById("highlight-toggle"),
      clearDataBtn: document.getElementById("clear-data-btn"),
      closeSettingsBtn: document.getElementById("close-settings-btn"),
      statsModal: document.getElementById("stats-modal"),
      statsContent: document.getElementById("stats-content"),
      closeStatsBtn: document.getElementById("close-stats-btn"),
    };
  },

  // Show screen
  showScreen(screenName) {
    document.querySelectorAll(".screen").forEach((screen) => {
      screen.classList.remove("active");
    });

    if (screenName === "home") {
      this.elements.homeScreen.classList.add("active");
    } else if (screenName === "game") {
      this.elements.gameScreen.classList.add("active");
    }
  },

  // Render Sudoku grid
  renderGrid(puzzle, currentBoard, givenCells) {
    this.elements.sudokuGrid.innerHTML = "";

    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        const cell = document.createElement("div");
        cell.className = "cell";
        cell.dataset.row = i;
        cell.dataset.col = j;

        const value = currentBoard[i][j];
        if (value !== 0) {
          cell.textContent = value;
          if (givenCells[i][j]) {
            cell.classList.add("given");
          } else {
            cell.classList.add("filled");
          }
        }

        this.elements.sudokuGrid.appendChild(cell);
      }
    }
  },

  // Update a single cell
  updateCell(row, col, value, isGiven = false) {
    const cell = this.elements.sudokuGrid.querySelector(
      `[data-row="${row}"][data-col="${col}"]`,
    );

    if (cell) {
      cell.textContent = value === 0 ? "" : value;
      cell.classList.remove("filled", "given");

      if (value !== 0) {
        if (isGiven) {
          cell.classList.add("given");
        } else {
          cell.classList.add("filled");
        }
      }
    }
  },

  // Highlight selected cell and related cells
  highlightCell(row, col, currentBoard = null, numberHighlightEnabled = true) {
    // Clear previous highlights
    document.querySelectorAll(".cell").forEach((cell) => {
      cell.classList.remove("selected", "highlight", "same-number");
    });

    if (row === null || col === null) return;

    // Highlight selected cell
    const selectedCell = this.elements.sudokuGrid.querySelector(
      `[data-row="${row}"][data-col="${col}"]`,
    );
    if (selectedCell) {
      selectedCell.classList.add("selected");
    }

    // Get the value of the selected cell
    const selectedValue = currentBoard ? currentBoard[row][col] : null;

    // Highlight same row, column, and box
    document.querySelectorAll(".cell").forEach((cell) => {
      const cellRow = Number.parseInt(cell.dataset.row);
      const cellCol = Number.parseInt(cell.dataset.col);

      if (cellRow === row || cellCol === col) {
        cell.classList.add("highlight");
      }

      // Highlight 3x3 box
      const boxRow = Math.floor(row / 3);
      const boxCol = Math.floor(col / 3);
      const cellBoxRow = Math.floor(cellRow / 3);
      const cellBoxCol = Math.floor(cellCol / 3);

      if (boxRow === cellBoxRow && boxCol === cellBoxCol) {
        cell.classList.add("highlight");
      }

      // Highlight cells with the same number (only if enabled)
      if (
        numberHighlightEnabled &&
        selectedValue !== null &&
        selectedValue !== 0 &&
        currentBoard
      ) {
        const cellValue = currentBoard[cellRow][cellCol];
        if (cellValue === selectedValue) {
          cell.classList.add("same-number");
        }
      }
    });
  },

  // Show conflicts
  showConflicts(conflicts) {
    // Clear previous conflicts
    document.querySelectorAll(".cell").forEach((cell) => {
      cell.classList.remove("conflict");
    });

    // Add conflict class to conflicting cells
    conflicts.forEach(({ row, col }) => {
      const cell = this.elements.sudokuGrid.querySelector(
        `[data-row="${row}"][data-col="${col}"]`,
      );
      if (cell) {
        cell.classList.add("conflict");
      }
    });
  },

  // Update timer display
  updateTimer(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    this.elements.timerDisplay.textContent = `${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  },

  // Show/hide timer
  toggleTimerDisplay(show) {
    this.elements.timerDisplay.style.display = show ? "block" : "none";
  },

  // Update difficulty display
  updateDifficultyDisplay(difficulty) {
    this.elements.difficultyDisplay.textContent = difficulty;
  },

  // Show/hide continue button
  showContinueButton(show) {
    this.elements.continueBtn.style.display = show ? "block" : "none";
  },

  // Show/hide hint button
  showHintButton(show) {
    this.elements.hintBtn.style.display = show ? "flex" : "none";
  },

  // Show modal
  showModal(modalName) {
    const modals = {
      pause: this.elements.pauseOverlay,
      victory: this.elements.victoryModal,
      settings: this.elements.settingsModal,
      stats: this.elements.statsModal,
    };

    const modal = modals[modalName];
    if (modal) {
      modal.classList.add("active");
      this.updateSafeAreaColor();
    }
  },

  // Hide modal
  hideModal(modalName) {
    const modals = {
      pause: this.elements.pauseOverlay,
      victory: this.elements.victoryModal,
      settings: this.elements.settingsModal,
      stats: this.elements.statsModal,
    };

    const modal = modals[modalName];
    if (modal) {
      modal.classList.remove("active");
      this.updateSafeAreaColor();
    }
  },

  // Show victory message
  showVictory(time, timerEnabled) {
    if (timerEnabled) {
      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      this.elements.victoryTime.textContent = `Completed in ${minutes}m ${seconds}s`;
    } else {
      this.elements.victoryTime.textContent = "Puzzle completed!";
    }
    this.showModal("victory");
  },

  // Update preferences UI
  updatePreferencesUI(prefs) {
    this.elements.timerToggle.checked = prefs.timerEnabled;
    this.elements.hintsToggle.checked = prefs.hintsEnabled;
    this.elements.highlightToggle.checked = prefs.numberHighlightEnabled;
    this.showHintButton(prefs.hintsEnabled);
  },

  // Render statistics
  renderStats(stats) {
    let html = `
      <div class="stats-header">
        <div class="stats-header-item">
          <span class="stats-header-label">Difficulty</span>
        </div>
        <div class="stats-header-item">
          <span class="stats-header-label">Completed</span>
        </div>
        <div class="stats-header-item">
          <span class="stats-header-label">Best Time</span>
        </div>
        <div class="stats-header-item">
          <span class="stats-header-label">Success Rate</span>
        </div>
      </div>
    `;

    Object.entries(stats).forEach(([difficulty, data]) => {
      const bestTime =
        data.bestTime && data.bestTime > 0
          ? `${Math.floor(data.bestTime / 60)}m ${data.bestTime % 60}s`
          : "-";

      const totalAttempts = data.attempts || data.completed;
      const successRate =
        totalAttempts > 0
          ? Math.round((data.completed / totalAttempts) * 100)
          : 0;

      html += `
        <div class="stat-row">
          <div class="stat-cell stat-difficulty">
            <span class="difficulty-name">${difficulty}</span>
          </div>
          <div class="stat-cell stat-completed">
            <span class="stat-number">${data.completed}</span>
          </div>
          <div class="stat-cell stat-time">
            <span class="stat-time-value">${bestTime}</span>
          </div>
          <div class="stat-cell stat-rate">
            <span class="stat-percentage">${
              totalAttempts > 0 ? successRate + "%" : "-"
            }</span>
          </div>
        </div>
      `;
    });

    this.elements.statsContent.innerHTML = html;
  },

  // Update safe area (status bar / notch) background when modals are shown on iOS
  // Approach: toggle a body class to change background-color so the extended
  // area behind the status bar matches the modal overlay. Also update the
  // theme-color meta tag for browsers that honor it (Chrome, Android, some iOS PWAs).
  updateSafeAreaColor() {
    const metaTheme = document.querySelector('meta[name="theme-color"]');

    if (document.querySelector(".overlay.active")) {
      document.body.classList.add("modal-open");
      if (metaTheme) metaTheme.setAttribute("content", "#1a1a1a");
    } else {
      document.body.classList.remove("modal-open");
      if (metaTheme) metaTheme.setAttribute("content", "#0A0A0A");
    }
  },
};

window.UIController = UIController;

if (window.__debug__) {
  console.debug("UIController exported to window");
}
